import React, { useState, useEffect } from "react";
import { X, MessageSquareMore } from "lucide-react";
import { getAuthToken, getUserProfile, getSourceName } from "../services/authService";
import {
  getApiBaseUrl,
  PORTAL_BASE_URL,
  withTrailingSlash,
} from "../config/urls";
import { usePageConfig } from "../hooks/usePageConfig";

const getSessionSourceName = () =>
  getSourceName() || getUserProfile()?.client_source_name || "";
const getSessionEmail = () => getUserProfile()?.email || "";

const CommentPartModal = ({
  isOpen,
  onClose,
  partData,
  assessmentData,
  onRefreshAssessment,
}) => {
  const { clientConfig } = usePageConfig();
  const [isAnimating, setIsAnimating] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setNewComment("");
    }
  }, [isOpen, partData]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const getPartId = (part) => {
    return (
      part?.part_id ||
      part?.partId ||
      part?.id ||
      part?.Part_ID ||
      part?.partID ||
      part?.part_ids?.[0] || // Get first part ID from array
      ""
    );
  };

  // Helper function to get the first available image from part data
  const getPartImage = (part) => {
    // Prioritize damage images first, then part images
    const damageImage =
      part?.part_images?.[0]?.upload_picture || part?.part_images?.[0]?.picture;
    const partImage =
      part?.part_images?.[0]?.upload_picture || part?.part_images?.[0]?.picture;

    return damageImage || partImage || "";
  };

  const partId = getPartId(partData);
  const partImage = getPartImage(partData);
  const partIdAsNumber = partId ? Number(partId) : "";

  // API call to save comment
  const saveCommentToAPI = async (newComment) => {
    const userEmail =
      getSessionEmail();

    const apiData = {
      data: [
        {
          is_fp: false,
          image: partImage,
          intensity: partData.Intensity,
          action: partData.Action || "No Damage",
          part_id: partIdAsNumber,
          part_name: partData?.part_name || "",
          partdamage_id: "",
          ref_num: assessmentData?.info?.ref_num || "",
          source_name: getSessionSourceName(),
          vehicle_id: assessmentData?.info?.id || "",
          edited_part_coords: "{}",
          user_id: userEmail,
          user_type: 10,
        },
      ],
      hello: "world",
      reviewerComment: {
        comment: newComment?.trim(),
        ref_num: assessmentData?.info?.ref_num || "",
        source_name: clientConfig.sourceName,
        vehicle_id: assessmentData?.info?.id || "",
        user_id: userEmail,
        user_type: 10,
      },
      verified_list: [],
    };

    const token = getAuthToken();
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/assess_update_review_history`,
        {
          method: "POST",
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Origin: PORTAL_BASE_URL,
            Referer: withTrailingSlash(PORTAL_BASE_URL),
          },
          body: `data=${encodeURIComponent(JSON.stringify(apiData))}`,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save comment");
      }

      const savedComment = await response.json();
      return savedComment;
    } catch (error) {
      console.error("Error saving comment:", error);
      throw error;
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setIsSubmitting(true);

    try {
      const commentData = {
        comment: newComment,
        user_id: getSessionEmail(),
        part_name: partData.part_name,
        created_at: new Date().toISOString(),
        damage_images: partData?.part_images || [], // optional
      };

      // Save to API
      const savedComment = await saveCommentToAPI(newComment);

      // Append locally
      const updatedComments = [
        ...(partData.edit_comments || []),
        { ...commentData, id: savedComment.id || Date.now() },
      ];

      // Update partData locally
      partData.edit_comments = updatedComments;

      // Reset form
      setNewComment("");

      // Trigger refresh if needed
      if (onRefreshAssessment) {
        onRefreshAssessment();
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
      onClick={handleClose}
    >
      <div
        className={`bg-background-secondary border-t border-border-secondary rounded-tl-[24px] rounded-tr-[24px] w-full h-[85vh] max-h-[85vh] lg:w-[65%] lg:h-[65vh] relative overflow-hidden flex flex-col transition-transform duration-300 ease-out ${
          isAnimating ? "translate-y-0" : "translate-y-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MessageSquareMore size={20} className="text-gray-700" />
            <h2 className="text-lg font-medium text-gray-900">Comments</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto py-4 px-[15%]">
          {partData?.edit_comments?.map((comment, index) => (
            <div key={index}>
              <div className="py-4">
                <div className="mb-1">
                  <span className="font-semibold text-gray-900">
                    {partData.part_name}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-gray-600">{comment.user_id}</span>
                  <span className="text-sm text-gray-400">•</span>
                  <span className="text-sm text-gray-400">{comment.created_at}</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{comment.comment}</p>
              </div>
              {index < partData.edit_comments.length - 1 && (
                <div className="border-b border-gray-200"></div>
              )}
            </div>
          ))}

          {/* Add Comment Section */}
          <div className="border-t border-gray-200 py-4">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={`Add a comment on ${partData?.part_name || "this part"}...`}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
              rows="3"
              disabled={isSubmitting}
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleAddComment}
                disabled={!newComment.trim() || isSubmitting}
                className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                  newComment.trim() && !isSubmitting
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentPartModal;
